import { URL_IMG } from '@/constants/ui'
import { getOrderInfoPlaceOrder, getOrderStatus } from '@/features/payment'
import { usePathname } from '@/libs/i18n/routing'
import { TypePopup, useUpsellStore } from '@/store/upsell'
import { getVND } from '@/utils/helpers'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import Image from 'next/image'
import Link from 'next/link'
import { useCallback, useEffect, useMemo } from 'react'
import { TProductItem, WrapGlobalFixedProduct } from './wrap-global-fixed-product'
import { motion } from 'framer-motion'
import { useUpsellCountdown } from '../hooks/use-upsell-countdown'

//#region PopupReturnCheckoutPage
export const PopupReturnCheckoutPage = () => {
  const pathname = usePathname()
  const isPaymentPage = pathname.includes('/payment')
  const { orderCode, clearOrderCode, type } = useUpsellStore()

  const clearOrderCodeSafe = useCallback(() => {
    const { orderCode } = useUpsellStore.getState()
    if (orderCode) {
      clearOrderCode()
    }
  }, [clearOrderCode])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['ORDER_INFO', orderCode],
    queryFn: () => getOrderInfoPlaceOrder(orderCode || ''),
    enabled: !!orderCode && type === TypePopup.PAYMENT,
    staleTime: 0,
    gcTime: 0,
  })

     useQuery({
       queryKey: ['ORDER_STATUS', orderCode],
       queryFn: async () => {
         if (!orderCode) return false
         return await getOrderStatus(orderCode).then((res) => {
           if (res.state == 'processing') {
             clearOrderCode()
           }
           return res
         })
       },
       refetchInterval: 1000 * 10,
       enabled: !!orderCode && type === TypePopup.PAYMENT,
       refetchOnWindowFocus: true,
     })

  const onExpire = () => {
    clearOrderCodeSafe()
  }
  // Sử dụng custom hook để quản lý countdown
  const { expireAfter } = useUpsellCountdown({
    type: TypePopup.PAYMENT,
    expireDate: data?.orderV2?.expire_at,
    onComplete: onExpire,
  })

  // Refresh data khi quay lại từ payment page
  useEffect(() => {
    if (orderCode && type === TypePopup.PAYMENT && !isPaymentPage) {
      refetch()
    }
  }, [orderCode, type, isPaymentPage, refetch])

  const total = useMemo(() => {
    if (!data?.orderV2?.grand_total) return 0
    return data?.orderV2?.grand_total?.value || 0
  }, [data?.orderV2?.grand_total])

  const convertedData: TProductItem[] = useMemo(() => {
    if (!data) return [] as TProductItem[]
    const tempResult = data?.orderV2?.items?.map((item) => ({
      product_name: item?.product?.name,
      add_qty: item?.quantity,
      checkout_price: item?.prices?.price?.value,
      final_price: item?.prices?.price?.value,
      product_image: item?.product?.image?.url,
      link: `/products/${item?.product?.url_key}`,
      attributes: item?.attributes || [],
    }))
    return tempResult as TProductItem[]
  }, [data])

  const caculatedCashbackCoin = useMemo(() => {
    if (!data?.orderV2) return 0
    const cashback = data?.orderV2?.affiliate_information?.cashback
    const total = data?.orderV2?.grand_total?.value
    if (!cashback || !total) return 0
    const cashbackCoin = Math.ceil((cashback * total) / 100)
    return cashbackCoin
  }, [data?.orderV2])

  const isHidePopup =
    !data || isLoading || isPaymentPage || expireAfter <= 0 || data?.orderV2?.items?.length === 0

  if (isHidePopup) return null

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      sx={{
        position: 'fixed',
        right: 0,
        top: '40vh',
        zIndex: 2,
      }}
    >
      <WrapGlobalFixedProduct
        data={convertedData}
        total={total}
        expireAfter={expireAfter}
        content={{
          titleOffer: <TitleOfferCustom />,
          flagTitle: (
            <>
              Thanh toán <Box component={'br'} sx={{ display: { xs: 'none', sm: 'block' } }} /> sẽ hết hạn:
            </>
          ),
          flagTitleMobile: (
            <>
              Thanh toán <Box component={'br'} /> sẽ hết hạn:
            </>
          ),
          buttonSubmit: (
            <ButtonSubmit cashbackCoin={caculatedCashbackCoin} total={total} order={orderCode || ''} />
          ),
        }}
      />
    </Box>
  )
}

//#region Title
const TitleOfferCustom = () => {
  return (
    <Typography
      sx={{
        fontSize: { xs: '0.875rem', md: '1rem', xxl: '1.125rem' },
        fontWeight: 300,
        color: 'inherit',
        lineHeight: 1.4,
      }}
    >
      Chỉ còn 1 bước thanh toán nữa sẽ hoàn tất{' '}
      <Box component={'span'} whiteSpace={'nowrap'}>
        đơn hàng
      </Box>
    </Typography>
  )
}

//#region Button Submit
const ButtonSubmit = ({
  cashbackCoin,
  total,
  order,
}: {
  cashbackCoin: number
  total: number
  order: string
}) => {
  return (
    <Button
      //    loading={isPending || isLoading}
      //    disabled={isPending || !cartItems.length}
      LinkComponent={Link}
      href={`/payment?order=${order}`}
      variant="contained"
      sx={{
        width: '100%',
        height: '100%',
        padding: '12px 24px',
        color: 'primary.contrastText',
        textTransform: 'none',
        '&.Mui-disabled': {
          '& img': {
            opacity: 0.5,
          },
        },
        '&.MuiButton-loading': {
          '& img': {
            opacity: 0,
          },
        },
      }}
    >
      <Stack>
        <Typography
          sx={{ fontSize: { xs: '0.875rem', md: '1rem', xl: '1.125rem' }, color: 'inherit', fontWeight: 600 }}
        >
          THANH TOÁN NGAY - {getVND(total, 'Đ')}
        </Typography>
        {cashbackCoin > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography
              component={'span'}
              sx={{
                fontSize: { xs: '12px', md: '14px' },
                lineHeight: 1.3,
                color: 'inherit',
                fontWeight: 300,
              }}
            >
              Nhận ngay{' '}
              <Box component={'span'} sx={{ fontWeight: 600 }}>
                {getVND(cashbackCoin, ' ')}
              </Box>{' '}
              xu
            </Typography>
            <Image
              style={{ display: 'inline-block', margin: '0 4px' }}
              width={16}
              height={16}
              src={URL_IMG + 'favicon-yellow.svg'}
              alt="Favicon"
            />
          </Box>
        )}
      </Stack>
    </Button>
  )
}
